import { Box, Tab, Tabs } from '@mui/material';
import { useId, useState } from 'react';
import { EvaluationCase } from '../EvaluationCase';
import type { EvaluationTabsProps } from './types';

/**
 * Every evaluation case, a tab apiece, so the cases can be browsed a click at a
 * time instead of scrolled past as one long page. **The prompt is not one of the
 * tabs**: it is the thing the cases are about, so it sits above them, always in view.
 *
 * **MUI's `Tabs` supplies the keyboard and the roles** — a tablist, tabs that say
 * which is selected, arrow keys between them — and what is added here is the part
 * it leaves to the caller: each tab names its panel and each panel is labelled by
 * its tab, so a screen reader announces "readme-disagrees-with-the-code, tab
 * panel" rather than an anonymous region.
 *
 * **Only the selected panel is in the document.** Rendering all of them and hiding
 * the rest would put every case's input and expected output in the page's text for
 * a find-in-page to hit and a screen reader to wander into.
 *
 * **The first case is open until another is chosen**, and the selection is
 * component state with the caller keying on the version to reset it. Which case is
 * showing is a view of the page and not an address anyone shares; the version is
 * the address. A selection that points at a case no longer in the list falls back to
 * the first rather than to an empty panel.
 *
 * Renders nothing for no cases: a tab bar with nothing to open reads as content that
 * failed to load.
 */
export const EvaluationTabs = ({ evaluations }: EvaluationTabsProps) => {
  const base = useId();
  const [requested, setRequested] = useState<string | undefined>(undefined);

  const valueOf = (id: number) => `evaluation-${String(id)}`;
  const first = evaluations[0];

  if (first === undefined) {
    return null;
  }

  const evaluation = evaluations.find((entry) => valueOf(entry.id) === requested) ?? first;
  const selected = valueOf(evaluation.id);

  const tabId = (value: string) => `${base}-tab-${value}`;
  const panelId = (value: string) => `${base}-panel-${value}`;

  return (
    <Box>
      <Tabs
        value={selected}
        onChange={(_event, value: string) => {
          setRequested(value);
        }}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Evaluations"
        sx={{ borderBottom: 1, borderColor: 'border.default' }}
      >
        {evaluations.map((entry) => (
          <Tab
            key={entry.id}
            value={valueOf(entry.id)}
            label={entry.name}
            id={tabId(valueOf(entry.id))}
            aria-controls={panelId(valueOf(entry.id))}
            sx={{
              // MUI upper-cases tab labels. These are file-style names a reader
              // has to recognise, and capitals would make them a different word.
              textTransform: 'none',
              maxWidth: 'none',
              fontWeight: 500,
              color: 'text.secondary',
              '&.Mui-selected': { color: 'text.primary' },
            }}
          />
        ))}
      </Tabs>

      <Box
        role="tabpanel"
        id={panelId(selected)}
        aria-labelledby={tabId(selected)}
        sx={{ marginTop: 6 }}
      >
        <EvaluationCase evaluation={evaluation} />
      </Box>
    </Box>
  );
};
